import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { toWapi } from '../util/api';
import Page from '../components/Page';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Http as HttpIcon,
  Save as SaveIcon,
  Security as SecurityIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  color: theme.palette.text.secondary,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
}));

const SOCKS_PROTOCOLS = ['socks4:', 'socks5:'];
const REGIONS = [
  { value: '', label: 'Default (India)' },
  { value: 'SA', label: 'South America' },
  { value: 'CO', label: 'Colombia' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'AR', label: 'Argentina' },
  { value: 'NAF', label: 'North Africa' },
];

// Bir radio seçeneği: buton + başlık + kısa açıklama; seçiliyken kendi kontrolü (varsa) hemen altında açılır.
// disabled=true ise seçilemez, başlığın yanında "Soon" etiketi görünür (henüz izlenmeyen/hazır olmayan yöntemler için).
const ProxyOption = ({ value, title, description, selectedMethod, onSelect, children, disabled }) => (
  <Box sx={{ mb: 2, opacity: disabled ? 0.5 : 1 }}>
    <FormControlLabel
      value={value}
      control={<Radio disabled={disabled} />}
      onClick={() => !disabled && onSelect(value)}
      label={
        <Box>
          <Box display="flex" alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{title}</Typography>
            {disabled && (
              <Box component="span" sx={{ ml: 1, px: 1, py: 0.25, borderRadius: 1, bgcolor: 'grey.300', fontSize: '0.7rem', fontWeight: 'bold' }}>
                SOON
              </Box>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">{description}</Typography>
        </Box>
      }
      sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { mt: 0.5 } }}
    />
    {!disabled && selectedMethod === value && children && (
      <Box sx={{ pl: 5, mt: 1 }}>{children}</Box>
    )}
  </Box>
);

const PhoneProxy = React.memo(({ product }) => {
  const { phone } = useParams();
  const history = useHistory();
  const { id, settings: { wapi_token } } = product;

  const [initialLoading, setInitialLoading] = useState(true);
  const [isValidPhone, setIsValidPhone] = useState(true);
  const [isBawa, setIsBawa] = useState(false);
  const [phoneInfo, setPhoneInfo] = useState(null); // { number, status, name } — hangi instance üzerinde işlem yapıldığını göstermek için

  const [selectedMethod, setSelectedMethod] = useState('none'); // 'none' | 'managed' | 'byop' | 'appProxy'
  const [customProxyValue, setCustomProxyValue] = useState('');
  const [proxyRegionValue, setProxyRegionValue] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [testingProxy, setTestingProxy] = useState(false);
  const [proxyTestResult, setProxyTestResult] = useState(null); // { ok, ip } | { ok:false, message }

  const [appProxyIp, setAppProxyIp] = useState(''); // instance'ın dedicated node IP'si — telefona girilecek adres
  const [appProxyIpMismatch, setAppProxyIpMismatch] = useState(false); // instance başka node'a taşınmış, telefondaki IP artık eski

  // Telefon geçerliliği: sadece bu id'nin bu ürüne ait olup olmadığına bakıyoruz — 'active' şartı YOK,
  // proxy ayarları telefon bağlanmadan önce de görülüp düzenlenebilmeli.
  const fetchPhones = useCallback(async () => {
    try {
      const response = await toWapi(`/api/${id}/listPhones`, wapi_token);
      if (response && response.status === 200 && Array.isArray(response.data)) {
        const phoneData = response.data.find((p) => p.id.toString() === phone);
        setIsValidPhone(!!phoneData);
        setIsBawa(!!(phoneData && phoneData.bawa));
        setPhoneInfo(phoneData || null);
      } else {
        setIsValidPhone(false);
      }
    } catch (error) {
      console.error('Error fetching phones:', error);
      setIsValidPhone(false);
    }
  }, [id, wapi_token, phone]);

  const fetchProxyState = useCallback(async () => {
    try {
      const [configRes, appProxyRes, mobileProxyRes] = await Promise.all([
        toWapi(`/api/${id}/${phone}/config`, wapi_token),
        toWapi(`/api/${id}/${phone}/appProxy`, wapi_token),
        toWapi(`/api/${id}/${phone}/mobileProxy`, wapi_token),
      ]);
      const cp = (configRes && configRes.data && configRes.data.config && configRes.data.config.customProxy) || '';
      const ap = !!(appProxyRes && appProxyRes.data && appProxyRes.data.appProxyEnabled);
      const mp = !!(mobileProxyRes && mobileProxyRes.data && mobileProxyRes.data.mobile_proxy);
      const region = (mobileProxyRes && mobileProxyRes.data && mobileProxyRes.data.proxy_region) || '';
      setCustomProxyValue(cp);
      setProxyRegionValue(region);
      setSelectedMethod(cp ? 'byop' : ap ? 'appProxy' : mp ? 'managed' : 'none');
      setAppProxyIp((appProxyRes && appProxyRes.data && appProxyRes.data.ip) || '');
      setAppProxyIpMismatch(!!(appProxyRes && appProxyRes.data && appProxyRes.data.ipMismatch));
    } catch (error) {
      console.error('Error fetching proxy state:', error);
    }
  }, [id, phone, wapi_token]);

  useEffect(() => {
    const load = async () => {
      setInitialLoading(true);
      await fetchPhones();
      await fetchProxyState();
      setInitialLoading(false);
    };
    load();
  }, [fetchPhones, fetchProxyState]);

  const isSocksOnNonBawa = useMemo(() => {
    if (isBawa) return false;
    try {
      return SOCKS_PROTOCOLS.includes(new URL(customProxyValue).protocol);
    } catch (e) {
      return false;
    }
  }, [customProxyValue, isBawa]);

  const handleMethodChange = useCallback((value) => {
    setSelectedMethod(value);
    setSaveSuccess(false);
  }, []);

  // Tek Save butonu: hangi seçenek seçiliyse ona ait API çağrısını yapar. Backend zaten üç yöntemi
  // (managed/BYOP/app-proxy) karşılıklı dışlıyor — biri set edilince diğerleri otomatik kapanıyor.
  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError('');
    setSaveSuccess(false);
    try {
      if (selectedMethod === 'none') {
        await Promise.all([
          toWapi(`/api/${id}/${phone}/config`, wapi_token, { customProxy: '' }),
          toWapi(`/api/${id}/${phone}/appProxy`, wapi_token, { appProxyEnabled: false }),
          toWapi(`/api/${id}/${phone}/mobileProxy`, wapi_token, { mobile_proxy: false }),
        ]);
      } else if (selectedMethod === 'byop') {
        const response = await toWapi(`/api/${id}/${phone}/config`, wapi_token, { customProxy: customProxyValue.trim() });
        if (!response || !response.data || !response.data.success) {
          setSaveError((response && response.data && response.data.message) || 'Failed to save.');
        }
      } else if (selectedMethod === 'appProxy') {
        await toWapi(`/api/${id}/${phone}/appProxy`, wapi_token, { appProxyEnabled: true });
      } else {
        await toWapi(`/api/${id}/${phone}/mobileProxy`, wapi_token, { mobile_proxy: true, proxy_region: proxyRegionValue });
      }
      await fetchProxyState();
      setSaveSuccess(true);
    } catch (error) {
      console.error('Error saving proxy settings:', error);
      setSaveError('Failed to save.');
    } finally {
      setSaving(false);
    }
  }, [id, phone, wapi_token, selectedMethod, customProxyValue, proxyRegionValue, fetchProxyState]);

  // Kaydedilmiş BYOP proxy'sini test eder — sadece kaydedilen customProxy'yi test eder, input'taki
  // henüz kaydedilmemiş değeri değil (bu yüzden önce Save gerekiyor).
  const handleTestProxy = useCallback(async () => {
    setTestingProxy(true);
    setProxyTestResult(null);
    try {
      const response = await toWapi(`/api/${id}/${phone}/proxyTest`, wapi_token);
      if (response && response.data && response.data.success) {
        setProxyTestResult({ ok: true, ip: response.data.ip });
      } else {
        setProxyTestResult({ ok: false, message: (response && response.data && response.data.message) || 'Test failed.' });
      }
    } catch (error) {
      setProxyTestResult({ ok: false, message: 'Test failed.' });
    } finally {
      setTestingProxy(false);
    }
  }, [id, phone, wapi_token]);

  return (
    <Page title="Phone Proxy" icon={<SecurityIcon />}>
      {initialLoading ? (
        <Container sx={{ mt: 4, textAlign: 'center' }}>
          <CircularProgress />
        </Container>
      ) : !isValidPhone ? (
        <Container maxWidth="sm" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>An error occurred</Typography>
          <Typography variant="body1" paragraph>
            Please make sure that this Phone ID is yours and that it is active.
          </Typography>
          <Button variant="contained" color="primary" onClick={() => history.push('/')}>Go to Homepage</Button>
        </Container>
      ) : (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mr: 1 }}>
              Instance #{phone}
            </Typography>
            <Chip size="small" label={phoneInfo && phoneInfo.status ? phoneInfo.status : 'unknown'} color={phoneInfo && phoneInfo.status === 'active' ? 'success' : 'default'} />
            <Chip size="small" variant="outlined" label={phoneInfo && phoneInfo.number ? phoneInfo.number : 'No number'} />
            {phoneInfo && phoneInfo.name && <Chip size="small" variant="outlined" label={phoneInfo.name} />}
            <Chip size="small" variant="outlined" label={isBawa ? 'Bawa' : 'Web'} />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledPaper elevation={3}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>Proxy</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose how this phone connects to WhatsApp. Only one option can be active at a time — selecting a new
                  one automatically turns off the previous one when you press Save.
                </Typography>

                <RadioGroup value={selectedMethod}>
                  <ProxyOption
                    value="none"
                    title="None"
                    description="No extra proxy is used — the connection goes out directly. Simplest option if you don't need to change your region."
                    selectedMethod={selectedMethod}
                    onSelect={handleMethodChange}
                  />

                  <ProxyOption
                    value="managed"
                    title="Managed Proxy"
                    description="Uses our own proxy. Only active during QR pairing, then turns itself off. May incur additional cost — contact support if you're unsure."
                    selectedMethod={selectedMethod}
                    onSelect={handleMethodChange}
                    disabled
                  >
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel id="proxy-region-label">Region</InputLabel>
                      <Select
                        labelId="proxy-region-label"
                        label="Region"
                        value={proxyRegionValue}
                        onChange={(e) => setProxyRegionValue(e.target.value)}
                      >
                        {REGIONS.map((r) => (
                          <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </ProxyOption>

                  <ProxyOption
                    value="byop"
                    title="Bring Your Own Proxy"
                    description="Enter your own SOCKS5 or HTTP(S) proxy. Unlike the managed proxy, this stays active for the entire connection (not just QR pairing), at no cost to us."
                    selectedMethod={selectedMethod}
                    onSelect={handleMethodChange}
                  >
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      placeholder="socks5://login:password@ip:port or http(s)://login:password@ip:port"
                      value={customProxyValue}
                      onChange={(e) => setCustomProxyValue(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HttpIcon sx={{ color: '#075E54' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {isSocksOnNonBawa && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        SOCKS4/SOCKS5 only works on Bawa-type phones — this phone is web-type, a SOCKS proxy will be silently ignored. Use http(s):// instead.
                      </Alert>
                    )}
                    {customProxyValue && (
                      <Box sx={{ mt: 1 }}>
                        <Button size="small" variant="outlined" onClick={handleTestProxy} disabled={testingProxy}>
                          {testingProxy ? 'Testing...' : 'Test Proxy'}
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          Save first — this tests the saved proxy, not unsaved changes.
                        </Typography>
                        {proxyTestResult && (
                          <Alert severity={proxyTestResult.ok ? 'success' : 'error'} sx={{ mt: 1 }}>
                            {proxyTestResult.ok ? `Connected — exit IP: ${proxyTestResult.ip}` : proxyTestResult.message}
                          </Alert>
                        )}
                      </Box>
                    )}
                  </ProxyOption>

                  <ProxyOption
                    value="appProxy"
                    title="In-App Proxy"
                    description="Enter our proxy address in your own WhatsApp app (Settings → Storage and Data → Proxy) so your phone connects from the same address as this instance."
                    selectedMethod={selectedMethod}
                    onSelect={handleMethodChange}
                  >
                    {appProxyIp ? (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            variant="outlined"
                            size="small"
                            value={appProxyIp}
                            InputProps={{ readOnly: true }}
                            sx={{ minWidth: 220 }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => navigator.clipboard.writeText(appProxyIp)}
                          >
                            Copy
                          </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          WhatsApp app → Settings → Storage and Data → Proxy → enter this address.
                        </Typography>
                        {selectedMethod === 'appProxy' && appProxyIpMismatch && (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            This instance moved to a new address since you last set this up. Update the Proxy field in
                            your WhatsApp app with the address above, or your phone-side proxy will stop matching.
                          </Alert>
                        )}
                      </>
                    ) : (
                      <Alert severity="info">This phone type does not have a dedicated proxy address.</Alert>
                    )}
                  </ProxyOption>
                </RadioGroup>

                {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}
                {saveSuccess && !saveError && <Alert severity="success" sx={{ mt: 2 }}>Saved.</Alert>}

                <Box sx={{ mt: 3 }}>
                  <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving} sx={{ bgcolor: '#25D366', color: 'white' }}>
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </Box>
              </StyledPaper>
            </Grid>
          </Grid>
        </Container>
      )}
    </Page>
  );
});

const stateToProps = (state) => ({
  access: state.access,
  location: state.router.location,
  product: state.product.maytapi,
});

export default connect(stateToProps)(PhoneProxy);
